// Spreadsheet export — PSD §8.5 (F8.5). State layer.

import type { ValuationResult, CapTable, Breakpoint } from '@backsolve/shared-types';

export interface SpreadsheetSheet {
  name: string;
  headers: string[];
  rows: (string | number)[][];
}

/**
 * Build the spreadsheet sheets from a valuation result.
 * Returns sheets for: Cap Table, Breakpoint Schedule, Allocation Waterfall, Headline.
 */
export function buildSpreadsheetSheets(result: ValuationResult): SpreadsheetSheet[] {
  const sheets: SpreadsheetSheet[] = [];

  // Cap Table
  const cap: CapTable = result.inputs.capTableSnapshot;
  sheets.push({
    name: 'Cap Table',
    headers: ['ID', 'Name', 'Kind', 'Shares Outstanding', 'Shares Authorized', 'Issue Price'],
    rows: cap.instruments.map((i) => [
      i.id,
      i.name,
      i.kind,
      i.sharesOutstanding,
      i.sharesAuthorized,
      i.issuePrice,
    ]),
  });

  // Breakpoint Schedule
  const bps: Breakpoint[] = result.breakpoints;
  sheets.push({
    name: 'Breakpoint Schedule',
    headers: ['Index', 'Equity Value Low', 'Equity Value High', 'Cause', 'Classes Sharing'],
    rows: bps.map((b) => [
      b.index,
      b.equityValueLow,
      b.equityValueHigh,
      b.cause,
      b.classesSharing.join(', '),
    ]),
  });

  // Allocation Waterfall
  sheets.push({
    name: 'Allocation Waterfall',
    headers: ['Breakpoint', 'Call Value High', 'Call Value Low', 'Tranche Value', 'Class', 'Value', 'Shares'],
    rows: result.allocation.flatMap((t) =>
      t.perClass.map((pc) => [
        t.breakpointIndex,
        t.callValueHigh,
        t.callValueLow,
        t.trancheValue,
        pc.instrumentId,
        pc.value,
        pc.shares,
      ]),
    ),
  });

  // Headline
  const h = result.headline;
  sheets.push({
    name: 'Headline',
    headers: ['Metric', 'Value'],
    rows: [
      ['Total Equity Value', h.totalEquityValue],
      ['Common Value (pre-DLOM)', h.commonValuePreDlom],
      ['Common Value (post-DLOM)', h.commonValuePostDlom],
      ['Fully Diluted Shares', h.fullyDilutedShares],
      ['Common FMV / share', h.commonFmvPerShare],
      ['Recommended Strike Price', h.recommendedStrikePrice],
      ['Preferred Price / share', h.preferredPricePerShare ?? 'n/a'],
      ['Safe Harbor Window (days)', h.safeHarborWindowDays],
    ],
  });

  return sheets;
}

/**
 * Generate a CSV string from a single sheet.
 */
export function sheetToCsv(sheet: SpreadsheetSheet): string {
  const lines = [sheet.headers.join(',')];
  for (const row of sheet.rows) {
    lines.push(row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
  }
  return lines.join('\n');
}

/**
 * Generate a multi-sheet CSV bundle (zipped not needed — returns a map of filename → CSV).
 */
export function exportToCsvBundle(result: ValuationResult): Record<string, string> {
  const sheets = buildSpreadsheetSheets(result);
  const out: Record<string, string> = {};
  for (const s of sheets) {
    out[`${s.name}.csv`] = sheetToCsv(s);
  }
  return out;
}

/**
 * Trigger a browser download of a CSV bundle as individual files (sequential).
 */
export function downloadCsvBundle(result: ValuationResult): void {
  const bundle = exportToCsvBundle(result);
  const base = `valuation_${result.valuationId}`;
  for (const [filename, csv] of Object.entries(bundle)) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}_${filename}`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
